import React from 'react';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  Label,
} from 'recharts';
import Grid from '@material-ui/core/Grid';
import Skeleton from '@material-ui/lab/Skeleton';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Switch from "@material-ui/core/Switch";
import { withStyles } from "@material-ui/core/styles";
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';


import BaseWeekStoreDashboardView from '../BaseWeekStoreDashboardView';
import Header from '../../components/utils/Header';
import FilterListView from '../../components/FilterListView';
import { formatFloat, unitFormatter } from '../../utils';
import DownloadButton from '../../components/utils/DownloadButton';
import DataFrameDownload from '../../components/utils/DataFrameDownload';
import { athenaBlue, athenaGreen, athenaRed, statRed } from '../../globals';
import DataFrame from "../../components/utils/DataFrame";


const styles = theme => ({
  negativeStock: {
    fontWeight: 'bold',
  },
  belowMinStock: {
    color: statRed,
  },
  aboveMaxStock: {
    color: athenaBlue,
  },
  closeToTargetStock: {
    color: athenaGreen,
  },
});


@withStyles(styles)
@observer
class SupplyView extends BaseWeekStoreDashboardView {
  static apiSubPath = '/supply/';

  static units = {
    TO: 'Tons',
    KPC: 'Pieces',
    L: 'Liters',
    kEUR: 'Euros',
  };
  static verboseUnits = {
    TO: 't',
    KPC: 'k',
    L: 'L',
    kEUR: 'k€',
  };

  @observable unit = Object.keys(this.constructor.units)[0];
  @observable technology = null;
  @observable actualsLevel = 'W-6';
  @observable forecastsLevel = 'W+38';

  _setData(data) {
    if ('week_store' in data && data.week_store) {
      this.weekStoreId = parseInt(data.week_store);
    }
    this.technology = data.technology;
  }

  _getQueryParams() {
    return {
      technology: this.technology,
    };
  }

  stockFormatter = unitFormatter(SupplyView.verboseUnits.TO, 1);
  kPiecesFormatter = unitFormatter(SupplyView.verboseUnits.KPC, 1);
  literFormatter = unitFormatter(SupplyView.verboseUnits.L, 1);
  euroFormatter = unitFormatter(SupplyView.verboseUnits.kEUR, 1);

  tooltipFormatter = (value, name) => {
    if (name.endsWith(' (Tons)')) {
      return this.stockFormatter(value);
    }
    if (name.endsWith(' (Pieces)')) {
      return this.kPiecesFormatter(value);
    }
    if (name.endsWith(' (Liters)')) {
      return this.litersFormatter(value);
    }
    if (name.endsWith(' (Euros)')) {
      return this.euroFormatter(value);
    }
    return formatFloat(value, 0);
  };

  xAxisFormatter = value => {
    value = `${value}`;
    return `${value.slice(0, 4)}-W${value.slice(4)}`;
  };

  yAxisFormatter = value => {
    if (this.unit === 'TO') {
      return this.stockFormatter(value);
    }
    if (this.unit === 'KPC') {
      return this.kPiecesFormatter(value);
    }
    if (this.unit === 'L') {
      return this.literFormatter(value);
    }
    if (this.unit === 'kEUR') {
      return this.euroFormatter(value);
    }
    return formatFloat(value, 0);
  };

  get verboseUnit() {
    return this.constructor.verboseUnits[this.unit];
  }

  onUnitChange = this.genericChangeHandler('unit', false);
  onTechnologyChange = this.genericChangeHandler('technology');

  @action onActualsLevelChange = event => {
    this.actualsLevel = event.target.checked ? 'W-1' : 'W-6';
  };

  @action onForecastsLevelChange = event => {
    this.forecastsLevel = event.target.checked ? 'W+38' : 'W+16';
  };

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={480} height={55} />;
    }
    return (
      <Grid container justify="flex-end" alignItems="center" spacing={4}>
        <Grid item>
          <Typography noWrap>
            W-6
            <Switch
              checked={this.actualsLevel === 'W-1'}
              onChange={this.onActualsLevelChange}
            />
            W-1
          </Typography>
        </Grid>
        <Grid item>
          <Typography noWrap>
            W+16
            <Switch
              checked={this.forecastsLevel === 'W+38'}
              onChange={this.onForecastsLevelChange}
            />
            W+38
          </Typography>
        </Grid>
        <Grid item>
          {this.weekStoreField}
        </Grid>
        <Grid item>
          {this.renderChoicesField(
            'Technology', this.data.technology_choices, this.technology,
            this.onTechnologyChange,
          )}
        </Grid>
        <Grid item>
          <TextField variant="outlined" select label="Unit" value={this.unit} onChange={this.onUnitChange}>
            {Object.entries(this.constructor.units).map(([value, name]) =>
              <MenuItem key={value} value={value}>{name}</MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
          <DataFrameDownload
            href={this.weekStore.getApiUrl('/supply/?' + this.getQueryString({ export: 'xlsx' }))}
            permission="parthenos.download_supply"
          />
        </Grid>
      </Grid>
    );
  }

  @computed get graphData() {
    let data = [...this.data.graph_data];
    if (this.actualsLevel === 'W-1') {
      data = data.slice(5);
    }
    if (this.forecastsLevel === 'W+16') {
      data = data.slice(0, data.length-10);
    }
    return data;
  }

  get chart() {
    if (!this.ready) {
      return <Skeleton variant="rect" height="100%" />;
    }
    const actualsEndWeek = this.data.graph_data.length >= 6 ? this.data.graph_data[5].WEK_COD : null;
    const { verboseUnit } = this;

    const CustomizedDot = (props) => {
      const { cx, cy, stroke, strokeWidth, payload } = props;
      return payload.WEK_COD === this.data.week_codes[-1] ? <circle cx={cx} cy={cy} r={2 * strokeWidth} fill={stroke}/> : null
    };

    return (
      <ComposedChart data={this.graphData} margin={{ top: 16, right: 16, bottom: 16, left: 32 }}>
        <XAxis dataKey="WEK_COD" name="week" tickFormatter={this.xAxisFormatter} />
        <YAxis yAxisId='left' tickFormatter={this.yAxisFormatter} />
        <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} />
        <RechartsTooltip formatter={this.tooltipFormatter} />
        {this.getArea(`active_capacity_${this.unit}`, { fill: '#dfdffc', name: `Active capacity (${verboseUnit})`, yAxisId: 'left' })}
        {this.getBar(`base_demand_${this.unit}`, { fill: '#f9ca24', name: `Demand (${verboseUnit})`, stackId: "demand", yAxisId: 'left' })}
        {this.getBar(`demand_in_past_${this.unit}`, { fill: '#946b21', name: `Demand in the past (${verboseUnit})`, stackId: "demand", yAxisId: 'left' })}
        {this.getBar(`sales_orders_${this.unit}`, { fill: '#dba348', name: `Sales orders (${verboseUnit})`, stackId: "demand", yAxisId: 'left' })}
        {this.getBar(`back_orders_${this.unit}`, { fill: '#8a000b', name: `Back orders (${verboseUnit})`, stackId: "demand", yAxisId: 'left' })}
        {this.getBar(`production_${this.unit}`, { fill: '#f5a623', name: `Production (${verboseUnit})`, yAxisId: 'left' })}
        {this.getLine(`stock_min_${this.unit}`, { stroke: '#00a8d7', strokeDasharray: '3 6', name: `Min stock (${verboseUnit})`, yAxisId: 'left' })}
        {this.getLine(`stock_target_${this.unit}`, { stroke: '#0069b3', strokeWidth: 2, strokeDasharray: '6 3', name: `Target stock (${verboseUnit})`, yAxisId: 'left'})}
        {this.getLine(`stock_max_${this.unit}`, { stroke: '#00a8d7', strokeDasharray: '3 6', name: `Max stock (${verboseUnit})`, yAxisId: 'left'})}
        {this.getLine(`previous_week_total_stock_${this.unit}`, { stroke: '#333', strokeWidth: 3, name:`Projected stock W-1 (${verboseUnit})`, yAxisId: 'left'})}
        {this.getLine(`total_stock_${this.unit}`, { stroke: '#fc5c65', strokeWidth: 4, name: `EOW Stock (${verboseUnit})`, yAxisId: 'left', dot: <CustomizedDot /> })}
        {actualsEndWeek && (
          <ReferenceLine
            yAxisId="left"
            x={actualsEndWeek}
            position="end"
            label={<Label position="top" fill={athenaRed}>Actuals end</Label>}
            strokeDasharray="3 3"
            stroke={athenaRed}
            isFront
          />
        )}
        <ReferenceLine yAxisId="left" y={0} strokeDasharray="3 3" />
      </ComposedChart>
    );
  }

  formatTableColumn = (serializedDataFrame, columnsRowIndex, columnName, columnIndex) => {
    columnName = `${columnName}`;
    return `${columnName.slice(0, 4)}-W${columnName.slice(4)}`
  };
  formatTableData = (serializedDataFrame, columnIndex, rowIndex, value) => formatFloat(value, 1);

  formatRecapTableData = (serializedDataFrame, columnIndex, rowIndex, value) => {
    const formattedValue = formatFloat(value, 1);
    let { index, data } = serializedDataFrame;
    index = index.map(([indexValue]) => indexValue);
    const currentIndex = index[rowIndex];
    const getOtherValue = indexValue => data[index.indexOf(indexValue)][columnIndex];
    for (const unit of [this.unit, 'DOH']) {
      if (currentIndex === `Stock (${unit})`) {
        const { classes } = this.props;
        let className = classes.closeToTargetStock;
        if (value < 0) {
          className = classes.negativeStock;
        } else if (value < getOtherValue(`Stock Min (${unit})`)) {
          className = classes.belowMinStock;
        } else if (value > getOtherValue(`Stock Max (${unit})`)) {
          className = classes.aboveMaxStock;
        }
        return <span className={className}>{formattedValue}</span>;
      }
    }
    return formattedValue;
  };

  get tables() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={800} />;
    }
    /** FIXME: Not really proud of this, but a hacky way to make the screen work
     * Similar to Line 189 in TechnologyView.js
    */
    if (this.data.consolidated_tables.recap === undefined) {
      return <h1>There is no data to show.</h1>
    }
    return (
      <Grid item>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{`Recap table (${this.unit})`}</Typography>
          </AccordionSummary>
          <AccordionDetails overflow='auto'>
            <DataFrame
              serializedDataFrame={this.data.consolidated_tables.recap[this.unit]}
              formatColumn={this.formatTableColumn}
              formatData={this.formatRecapTableData}
              maxHeight={600}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{`Stock coverage (${this.unit})`}</Typography>
          </AccordionSummary>
          <AccordionDetails overflow='auto'>
            <DataFrame
              serializedDataFrame={this.data.consolidated_tables.stock[this.unit]}
              formatColumn={this.formatTableColumn}
              formatData={this.formatRecapTableData}
              maxHeight={600}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{`Demand @Factory (${this.unit})`}</Typography>
          </AccordionSummary>
          <AccordionDetails overflow='auto'>
            <DataFrame
              serializedDataFrame={this.data.consolidated_tables.demand[this.unit]}
              formatColumn={this.formatTableColumn}
              formatData={this.formatTableData}
              maxHeight={600}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{`Production @Line (${this.unit})`}</Typography>
          </AccordionSummary>
          <AccordionDetails overflow='auto'>
            <DataFrame
              serializedDataFrame={this.data.consolidated_tables.production[this.unit]}
              formatColumn={this.formatTableColumn}
              formatData={this.formatTableData}
              maxHeight={600}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{`Stock @Factory (${this.unit})`}</Typography>
          </AccordionSummary>
          <AccordionDetails overflow='auto'>
            <DataFrame
              serializedDataFrame={this.data.consolidated_tables.factory_stock[this.unit]}
              formatColumn={this.formatTableColumn}
              formatData={this.formatTableData}
              maxHeight={600}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{'Capacity @Line (hours)'}</Typography>
          </AccordionSummary>
          <AccordionDetails overflow='auto'>
            <DataFrame
              serializedDataFrame={this.data.consolidated_tables.capacity['hours']}
              formatColumn={this.formatTableColumn}
              formatData={this.formatTableData}
              maxHeight={600}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{`Capacity @Line (${this.unit})`}</Typography>
          </AccordionSummary>
          <AccordionDetails overflow='auto'>
            <DataFrame
              serializedDataFrame={this.data.consolidated_tables.capacity[this.unit]}
              formatColumn={this.formatTableColumn}
              formatData={this.formatTableData}
              maxHeight={600}
            />
          </AccordionDetails>
        </Accordion>

      </Grid>
    );
  }

  renderContent() {
    if (!this.ready) {
      return <Grid item><Skeleton variant="rect" height={550} /></Grid>;
    }
    const { classes } = this.props;
    if (this.technology === null) {
      return (
        <Grid item container direction="column" wrap="nowrap" justify="center" className={classes.emptyTechnology}>
          <Grid item>Please select a technology</Grid>
        </Grid>
      );
    }
    return (
      <>
        <Grid item>
          <Box overflow="hidden">
            <Paper>
              <ResponsiveContainer aspect={21 / 9}>{this.chart}</ResponsiveContainer>
            </Paper>
          </Box>
        </Grid>
        <Grid item>
          {this.tables}
        </Grid>
      </>
    );
  }
  render() {
    return (
      <Grid container direction="column" wrap="nowrap" spacing={4}>
        <Grid item>
          <Grid container justify="space-between" alignItems="center" spacing={1} wrap="nowrap">
            <Grid item><Header title="Supply" /></Grid>
            <Grid item>{this.controls}</Grid>
          </Grid>
        </Grid>
        {this.renderContent()}
      </Grid>
    );
  }
}


export default SupplyView;
